import { useEffect } from "react";
import { toast } from "sonner";
import { TOTAL_CHANNELS, clamp, padCh } from "@/components/daw/dawHelpers";
import {
  transport as bridgeTransport,
  getLatencyOffset as getBridgeLatency,
  onTransport as onBridgeTransport,
  getReverseSync as getBridgeReverseSync,
  getMidiSync as getBridgeMidiSync,
} from "@/lib/bridgeClient";

/**
 * Transport controls — extracted verbatim from ELPMStudioDAW.
 * Play / stop, Desktop-Bridge reverse sync + CC#7 broadcast, A↔B loop
 * range handlers, Sync/Quantize toasts and the status-pill jump/rewind.
 */
export default function useTransportHandlers({
  engine,
  playing,
  setPlaying,
  loadedCount,
  setPlayhead,
  maxDur,
  setMaxDur,
  masterVol,
  setMasterVol,
  loopEnabled,
  setLoopEnabled,
  loopRange,
  setLoopRange,
  visibleChannels,
  setVisibleChannels,
  setSelectedChannel,
}) {
  const handlePlay = async () => {
    if (loadedCount === 0) {
      toast.message("Load or record audio first", { description: "Upload a sample to any channel to start playback." });
      return;
    }
    if (playing) {
      engine.pause();
      setPlaying(false);
      bridgeTransport.pause();
    } else {
      // iter-236 — Bridge latency offset. Fire MMC + Clock to Ableton
      // IMMEDIATELY but defer our own audio start by N ms.
      const offsetMs = getBridgeLatency();
      bridgeTransport.play(engine.bpm || 120);
      if (offsetMs > 0) {
        await new Promise((res) => setTimeout(res, offsetMs));
      }
      await engine.play();
      setPlaying(true);
    }
  };
  const handleStop = () => {
    engine.stop();
    setPlaying(false);
    setPlayhead(0);
    bridgeTransport.stop();
  };

  // iter-240 — Reverse Sync (DAW → ELPM), gated by the Reverse Sync
  // preference. iter-243 — inbound CC + Note events when MIDI Sync is on.
  useEffect(() => {
    const unsubscribe = onBridgeTransport(async (msg) => {
      const ev = (msg.event || "").toLowerCase();
      try {
        if (["play", "pause", "stop", "record", "bpm"].includes(ev)) {
          if (!getBridgeReverseSync()) return;
          if (ev === "play" || ev === "record") {
            if (loadedCount === 0) return;
            if (!engine.playing) {
              await engine.play();
              setPlaying(true);
              toast.message("⇦ DAW play", { description: "ELPM follows your DAW transport." });
            }
          } else if (ev === "pause") {
            if (engine.playing) { engine.pause(); setPlaying(false); }
          } else if (ev === "stop") {
            engine.stop(); setPlaying(false); setPlayhead(0);
            toast.message("⇦ DAW stop");
          } else if (ev === "bpm" && typeof msg.bpm === "number") {
            if (engine.bpm !== msg.bpm) engine.bpm = msg.bpm;
          }
        }
        if (ev === "cc") {
          if (!getBridgeMidiSync()) return;
          const num = msg.number, val = msg.value;
          if (typeof num !== "number" || typeof val !== "number") return;
          // CC#7 = Volume → master volume (0-127 → 0-100).
          if (num === 7) {
            const next = Math.round((val / 127) * 100);
            setMasterVol(next);
            engine.setMasterVolume(next);
          }
          // eslint-disable-next-line no-console
          console.debug(`[bridge] CC#${num} = ${val}`);
        }
        if (ev === "note") {
          if (!getBridgeMidiSync()) return;
          window.dispatchEvent(new CustomEvent("elpm:bridge-midi-note", {
            detail: { number: msg.number, velocity: msg.velocity, on: msg.on, channel: msg.channel },
          }));
        }
      } catch (e) { void e; }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, loadedCount]);

  // iter-243 — broadcast master volume changes as CC#7 when MIDI Sync is
  // enabled. Debounced to avoid flooding the wire.
  useEffect(() => {
    if (!getBridgeMidiSync()) return;
    const t = setTimeout(() => {
      const cc = Math.round((masterVol / 100) * 127);
      try { bridgeTransport.cc(7, cc, 0); } catch (e) { void e; }
    }, 40);
    return () => clearTimeout(t);
  }, [masterVol]);

  // First time audio gets loaded (maxDur 0 → >0), default loop range to the
  // full mix. Later maxDur changes extend the end only if the user hasn't
  // already shrunk the window.
  useEffect(() => {
    if (maxDur <= 0) return;
    setLoopRange((prev) => {
      if (prev.end === 0) {
        const next = { start: 0, end: maxDur };
        if (loopEnabled) engine.setLoop(true, next.start, next.end);
        return next;
      }
      const extended = prev.end >= engine.maxDuration - 0.01 ? maxDur : prev.end;
      const next = { start: prev.start, end: Math.max(prev.start + 0.1, Math.min(maxDur, extended)) };
      if (loopEnabled) engine.setLoop(true, next.start, next.end);
      return next;
    });
    // engine is a stable singleton; loopEnabled is read inside the updater.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDur]);

  const handleToggleLoop = () => {
    setLoopEnabled((prev) => {
      const next = !prev;
      const start = loopRange.start;
      const end = loopRange.end > 0 ? loopRange.end : engine.maxDuration || 0;
      engine.setLoop(next, start, end);
      toast.message(next ? "Loop ON" : "Loop OFF", {
        description: next ? "Drag A↔B markers on the timeline to set the loop window." : "Playback runs to end then stops.",
      });
      return next;
    });
  };

  const handleLoopRangeChange = (start, end) => {
    setLoopRange({ start, end });
    if (loopEnabled) engine.setLoop(true, start, end);
  };

  const handleLoopReset = () => {
    const end = engine.maxDuration || 0;
    setLoopRange({ start: 0, end });
    if (loopEnabled) engine.setLoop(true, 0, end);
    toast.message("A↔B reset", { description: "Loop now spans the full mix." });
  };

  /* ─── Sync / Quantize handlers ─── */
  const handleSynced = ({ ok, reason, loopSeconds }) => {
    if (!ok && reason === "no-audio") {
      toast.message("Load a clip first", { description: "Auto-Sync needs at least one channel with audio." });
      return;
    }
    setLoopEnabled(true);
    setLoopRange({ start: 0, end: loopSeconds });
    setMaxDur(Math.max(engine.maxDuration, loopSeconds));
    toast.success("Loops synced", {
      description: `Every loaded clip now loops to ${loopSeconds.toFixed(2)}s at ${engine.tempo} BPM.`,
    });
  };
  const handleQuantized = ({ ok, reason, rate, targetSeconds, channel, bars }) => {
    if (!ok && reason === "no-buffer") {
      toast.message("Select a channel with audio first");
      return;
    }
    toast.success(`CH ${padCh(channel)} quantized → ${bars} bar${bars === 1 ? "" : "s"}`, {
      description: `Playback rate ${rate.toFixed(3)}× · target ${targetSeconds.toFixed(2)}s`,
    });
  };
  const handleSyncCleared = () => {
    toast.message("Sync cleared", { description: "All channels back to their natural rate." });
  };

  /* ─── Status pill handlers ─── */
  const handleLoadedClick = () => {
    const firstLoaded = engine.channels.findIndex((c) => c.buffer);
    if (firstLoaded === -1) {
      toast.message("No audio loaded yet", { description: "Drop a sample onto any channel to get started." });
      return;
    }
    setSelectedChannel(firstLoaded);
    if (firstLoaded >= visibleChannels) {
      setVisibleChannels(clamp(firstLoaded + 8, 8, TOTAL_CHANNELS));
    }
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="channel-strip-${firstLoaded}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    });
    toast.message(`Jumped to CH ${padCh(firstLoaded)}`);
  };
  const handleLengthClick = () => {
    if (maxDur <= 0) return;
    engine.seekTo(0);
    setPlayhead(0);
    toast.message("Rewound to 0:00");
  };

  return {
    handlePlay,
    handleStop,
    handleToggleLoop,
    handleLoopRangeChange,
    handleLoopReset,
    handleSynced,
    handleQuantized,
    handleSyncCleared,
    handleLoadedClick,
    handleLengthClick,
  };
}
